import { AdminUsersPage } from "@/components/admin/users-page";
import { AdminSidebar } from "@/components/layout/admin-sidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger
} from "@/components/ui/sidebar";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth.store";
import { useFeedbackStore } from "@/stores/feedback.store";
import { usePromptsStore } from "@/stores/prompts.store";
import { useWordExportStore } from "@/stores/word-export.store";
import {
  MAX_WORD_TEMPLATE_SIZE,
  WORD_EXPORT_PLACEHOLDER_TOKEN,
  type ReadMessageFeedbackWithContextDto,
  type ReadPromptDto
} from "@package/types";
import { createRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import {
  Info,
  Loader2,
  Pencil,
  Plus,
  Power,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  Upload
} from "lucide-react";
import { type ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { rootRoute } from "./__root";

// Layout Route
export const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  beforeLoad: () => {
    const { isAuthenticated, user } = useAuthStore.getState();

    if (!isAuthenticated) {
      throw redirect({ to: "/login" });
    }

    if (user?.role !== "MODERATOR" && user?.role !== "ADMIN") {
      throw redirect({ to: "/chats" });
    }
  },
  component: AdminLayout
});

// Index Route (redirect to prompt)
export const adminIndexRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/admin/prompt" });
  }
});

// Prompt Route
export const adminPromptRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/prompt",
  component: AdminPromptPage
});

// Vector Storage Route
export const adminVectorStorageRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/vector-storage",
  component: AdminVectorStoragePage
});

// Users Route (ADMIN only, the rest of the panel is open to MODERATOR as well)
export const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/users",
  beforeLoad: () => {
    if (useAuthStore.getState().user?.role !== "ADMIN") {
      throw redirect({ to: "/admin/prompt" });
    }
  },
  component: AdminUsersPage
});

export const adminWordExportRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/word-export",
  component: AdminWordExportPage
});

// Feedback Route (ADMIN only, it manages the memory shared by every user)
export const adminFeedbackRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/feedback",
  beforeLoad: () => {
    if (useAuthStore.getState().user?.role !== "ADMIN") {
      throw redirect({ to: "/admin/prompt" });
    }
  },
  component: AdminFeedbackPage
});

function AdminLayout() {
  return (
    <SidebarProvider>
      <AdminSidebar />
      <SidebarInset>
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}

function AdminPromptPage() {
  const { t } = useTranslation();
  const prompts = usePromptsStore((s) => s.prompts);
  const isLoading = usePromptsStore((s) => s.isLoading);
  const loadPrompts = usePromptsStore((s) => s.loadPrompts);
  const loadDefault = usePromptsStore((s) => s.loadDefault);
  const createPrompt = usePromptsStore((s) => s.createPrompt);
  const updatePrompt = usePromptsStore((s) => s.updatePrompt);
  const deletePrompt = usePromptsStore((s) => s.deletePrompt);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const [editPrompt, setEditPrompt] = useState<ReadPromptDto | null>(null);
  const [editName, setEditName] = useState("");
  const [editContent, setEditContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [promptToDelete, setPromptToDelete] = useState<ReadPromptDto | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const [defaultPromptOpen, setDefaultPromptOpen] = useState(false);

  const defaultContent = usePromptsStore((s) => s.defaultContent);
  const hasActivePrompt = prompts.some((p) => p.isActive);

  useEffect(() => {
    loadPrompts();
    loadDefault();
  }, [loadPrompts, loadDefault]);

  const handleCreate = async () => {
    if (!createName.trim()) return;
    setIsCreating(true);
    try {
      await createPrompt(createName.trim());
      toast.success(t("admin.promptCreated"));
      setCreateOpen(false);
      setCreateName("");
    } catch {
      toast.error(t("common.error"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = (prompt: ReadPromptDto) => {
    setEditPrompt(prompt);
    setEditName(prompt.name);
    setEditContent(prompt.content);
  };

  const handleSave = async () => {
    if (!editPrompt || !editName.trim() || !editContent.trim()) return;
    setIsSaving(true);
    try {
      await updatePrompt(editPrompt.id, {
        name: editName.trim(),
        content: editContent.trim()
      });
      toast.success(t("admin.promptUpdated"));
      setEditPrompt(null);
    } catch {
      toast.error(t("common.error"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (prompt: ReadPromptDto) => {
    try {
      await updatePrompt(prompt.id, { isActive: !prompt.isActive });
      toast.success(
        prompt.isActive
          ? t("admin.promptDeactivated")
          : t("admin.promptActivated")
      );
    } catch {
      toast.error(t("common.error"));
    }
  };

  const handleDelete = async () => {
    if (!promptToDelete) return;
    setIsDeleting(true);
    try {
      await deletePrompt(promptToDelete.id);
      toast.success(t("admin.promptDeleted"));
      setPromptToDelete(null);
    } catch {
      toast.error(t("common.error"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex w-full items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-4"
            />
            <span className="font-medium">{t("admin.prompt")}</span>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            {t("admin.createPrompt")}
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
        {!hasActivePrompt && !isLoading && (
          <div
            className="flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-border bg-muted/50 p-3 text-sm text-muted-foreground hover:bg-muted"
            onClick={() => setDefaultPromptOpen(true)}
          >
            <Info className="size-4 shrink-0" />
            {t("admin.defaultPromptInfo")}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-hidden rounded-md border">
            <ScrollArea className="h-full">
              <Table className="table-fixed">
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead className="w-[200px]">
                      {t("admin.promptName")}
                    </TableHead>
                    <TableHead>{t("admin.promptContent")}</TableHead>
                    <TableHead className="w-[180px]">
                      {t("admin.lastUpdated")}
                    </TableHead>
                    <TableHead className="w-[120px] text-right">
                      {t("admin.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prompts.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="h-24 text-center text-muted-foreground"
                      >
                        {t("admin.noPrompts")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    prompts.map((prompt) => (
                      <TableRow key={prompt.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span className="truncate">{prompt.name}</span>
                            {prompt.isActive && (
                              <Badge variant="default" className="shrink-0">
                                {t("admin.active")}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-0">
                          <p className="truncate text-sm text-muted-foreground">
                            {prompt.content}
                          </p>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(prompt.updatedAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEdit(prompt)}
                                >
                                  <Pencil className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t("admin.editPrompt")}
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleToggleActive(prompt)}
                                >
                                  <Power
                                    className={
                                      prompt.isActive
                                        ? "size-4 text-success"
                                        : "size-4 text-muted-foreground"
                                    }
                                  />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {prompt.isActive
                                  ? t("admin.deactivate")
                                  : t("admin.activate")}
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setPromptToDelete(prompt)}
                                >
                                  <Trash2 className="size-4 text-destructive" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t("admin.deletePrompt")}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.createPrompt")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="prompt-name">{t("admin.promptName")}</Label>
            <Input
              id="prompt-name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={isCreating}
            >
              {t("header.cancel")}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isCreating || !createName.trim()}
            >
              {isCreating && <Loader2 className="size-4 animate-spin mr-2" />}
              {t("header.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={!!editPrompt}
        onOpenChange={(open) => !open && setEditPrompt(null)}
      >
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("admin.editPrompt")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">{t("admin.promptName")}</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-content">{t("admin.promptContent")}</Label>
              <Textarea
                id="edit-content"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[300px] max-h-[50vh] font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditPrompt(null)}
              disabled={isSaving}
            >
              {t("header.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !editName.trim() || !editContent.trim()}
            >
              {isSaving && <Loader2 className="size-4 animate-spin mr-2" />}
              {t("header.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog
        open={!!promptToDelete}
        onOpenChange={(open) => !open && setPromptToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.deletePromptTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.deletePromptDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("header.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="size-4 animate-spin mr-2" />}
              {t("admin.deletePrompt")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Default Prompt Dialog */}
      <Dialog open={defaultPromptOpen} onOpenChange={setDefaultPromptOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("admin.defaultPrompt")}</DialogTitle>
          </DialogHeader>
          <pre className="whitespace-pre-wrap break-words rounded-md bg-muted p-4 font-mono text-sm">
            {defaultContent}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminVectorStoragePage() {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <span className="font-medium">{t("admin.vectorStorage")}</span>
        </div>
      </header>

      <div className="flex flex-1 items-center justify-center p-8">
        <p className="text-muted-foreground">{t("admin.vectorStorage")}</p>
      </div>
    </div>
  );
}

function AdminWordExportPage() {
  const { t } = useTranslation();
  const config = useWordExportStore((s) => s.config);
  const isLoading = useWordExportStore((s) => s.isLoading);
  const loadConfig = useWordExportStore((s) => s.loadConfig);
  const updateConfig = useWordExportStore((s) => s.updateConfig);
  const uploadTemplate = useWordExportStore((s) => s.uploadTemplate);
  const deleteTemplate = useWordExportStore((s) => s.deleteTemplate);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const isEnabled = config?.isEnabled ?? false;

  const handleToggleEnabled = async () => {
    setIsSaving(true);
    try {
      await updateConfig(!isEnabled);
      toast.success(t("admin.wordExportUpdated"));
    } catch {
      toast.error(t("common.error"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";

    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".docx")) {
      toast.error(t("admin.wordExportInvalidFile"));
      return;
    }

    if (file.size > MAX_WORD_TEMPLATE_SIZE) {
      toast.error(t("admin.wordExportFileTooLarge"));
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      await uploadTemplate(selectedFile);
      toast.success(t("admin.wordExportTemplateUploaded"));
      setSelectedFile(null);
    } catch (error) {
      toast.error(t("admin.wordExportUploadFailed"), {
        description: error instanceof Error ? error.message : undefined
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteTemplate = async () => {
    setIsDeleting(true);
    try {
      await deleteTemplate();
      toast.success(t("admin.wordExportTemplateDeleted"));
    } catch {
      toast.error(t("common.error"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <span className="font-medium">{t("admin.wordExport")}</span>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
        {isLoading && !config ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <section className="rounded-md border border-border p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="font-medium">
                    {t("admin.wordExportEnabled")}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {config?.canExport
                      ? t("admin.wordExportReady")
                      : t("admin.wordExportNotReady")}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isEnabled}
                  onClick={handleToggleEnabled}
                  disabled={isSaving}
                  className={cn(
                    "relative h-6 w-11 shrink-0 rounded-full border border-border transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50",
                    isEnabled ? "bg-primary" : "bg-muted"
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 left-0.5 size-5 rounded-full bg-background shadow-sm transition-transform",
                      isEnabled ? "translate-x-5" : "translate-x-0"
                    )}
                  />
                  <span className="sr-only">
                    {t("admin.wordExportEnabled")}
                  </span>
                </button>
              </div>
            </section>

            <section className="rounded-md border border-border p-4">
              <div className="flex flex-col gap-4">
                <div>
                  <h2 className="font-medium">
                    {t("admin.wordExportTemplate")}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("admin.wordExportPlaceholder")}{" "}
                    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                      {WORD_EXPORT_PLACEHOLDER_TOKEN}
                    </code>
                  </p>
                </div>

                {config?.template ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-muted/50 p-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {config.template.filename}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(config.template.size)} ·{" "}
                        {new Date(config.template.updatedAt).toLocaleString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeleteTemplate}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Trash2 className="size-4 text-destructive" />
                      )}
                      {t("admin.wordExportDeleteTemplate")}
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
                    {t("admin.wordExportNoTemplate")}
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="word-template">
                    {t("admin.wordExportSelectTemplate")}
                  </Label>
                  <Input
                    id="word-template"
                    type="file"
                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                  {selectedFile && (
                    <p className="text-sm text-muted-foreground">
                      {selectedFile.name} · {formatFileSize(selectedFile.size)}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading}
                  >
                    {isUploading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Upload className="size-4" />
                    )}
                    {t("admin.wordExportUploadTemplate")}
                  </Button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function AdminFeedbackPage() {
  const { t } = useTranslation();
  const feedbacks = useFeedbackStore((s) => s.feedbacks);
  const isLoading = useFeedbackStore((s) => s.isLoading);
  const loadFeedbacks = useFeedbackStore((s) => s.loadFeedbacks);
  const deleteFeedback = useFeedbackStore((s) => s.deleteFeedback);

  const [feedbackToDelete, setFeedbackToDelete] =
    useState<ReadMessageFeedbackWithContextDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  const handleDelete = async () => {
    if (!feedbackToDelete) return;
    setIsDeleting(true);
    try {
      await deleteFeedback(feedbackToDelete.id);
      toast.success(t("admin.feedbackDeleted"));
      setFeedbackToDelete(null);
    } catch {
      toast.error(t("common.error"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <span className="font-medium">{t("admin.feedback")}</span>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
        <div className="flex shrink-0 items-center gap-2 rounded-lg border border-border bg-muted/50 p-3 text-sm text-muted-foreground">
          <Info className="size-4 shrink-0" />
          {t("admin.feedbackInfo")}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-hidden rounded-md border">
            <ScrollArea className="h-full">
              <Table className="table-fixed">
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead className="w-[110px]">
                      {t("admin.feedbackRating")}
                    </TableHead>
                    <TableHead>{t("admin.feedbackComment")}</TableHead>
                    <TableHead className="w-[200px]">
                      {t("admin.feedbackChat")}
                    </TableHead>
                    <TableHead className="w-[140px]">
                      {t("admin.feedbackAuthor")}
                    </TableHead>
                    <TableHead className="w-[180px]">
                      {t("admin.lastUpdated")}
                    </TableHead>
                    <TableHead className="w-[80px] text-right">
                      {t("admin.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedbacks.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="h-24 text-center text-muted-foreground"
                      >
                        {t("admin.noFeedback")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    feedbacks.map((feedback) => (
                      <TableRow key={feedback.id}>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              "gap-1",
                              feedback.rating === "POSITIVE"
                                ? "border-success/40 text-success"
                                : "border-destructive/40 text-destructive"
                            )}
                          >
                            {feedback.rating === "POSITIVE" ? (
                              <ThumbsUp className="size-3" />
                            ) : (
                              <ThumbsDown className="size-3" />
                            )}
                            {feedback.rating === "POSITIVE"
                              ? t("admin.feedbackGood")
                              : t("admin.feedbackBad")}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-0">
                          {feedback.comment ? (
                            <p className="truncate text-sm">
                              {feedback.comment}
                            </p>
                          ) : (
                            <p className="truncate text-sm text-muted-foreground italic">
                              {t("admin.feedbackNoComment")}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="max-w-0">
                          <Link
                            to="/chats/$chatId"
                            params={{ chatId: feedback.chatId }}
                            className="block truncate text-sm text-muted-foreground hover:text-foreground hover:underline"
                          >
                            {feedback.chatTitle || t("chats.noTitle")}
                          </Link>
                        </TableCell>
                        <TableCell className="max-w-0">
                          <p className="truncate text-sm text-muted-foreground">
                            {feedback.userName}
                          </p>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(feedback.updatedAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setFeedbackToDelete(feedback)}
                              >
                                <Trash2 className="size-4 text-destructive" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("admin.feedbackDelete")}
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}
      </div>

      <AlertDialog
        open={!!feedbackToDelete}
        onOpenChange={(open) => !open && setFeedbackToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.feedbackDeleteTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.feedbackDeleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("header.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="size-4 animate-spin mr-2" />}
              {t("admin.feedbackDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}
